import { Select, Tooltip } from 'antd';
import type { SelectProps, RefSelectProps } from 'antd';
import React, { forwardRef } from 'react';
import { legacyFillColor, legacyTextColor } from '@/utils/legacyColor';

const { Option } = Select;

interface SelectOption {
  value?: string | number;
  label?: string;
  disabled?: boolean;
  tooltipReason?: string;
}

interface CustomSelectProps extends Omit<SelectProps, 'options'> {
  allowClear?: boolean;
  mode?: 'multiple' | 'tags';
  width?: string;
  height?: string;
  border?: string;
  bgColor?: string;
  textColor?: string;
  borderRadius?: string;
  placeholder?: string;
  placeholderColor?: string;
  lineHeight?: string;
  value?: unknown;
  marginTop?: string;
  options?: SelectOption[];
  boxShadow?: string;
  disabled?: boolean;
  disableOption?: boolean;
  optionName?: string | number;
  optionClassName?: string;
}

const CustomSelect = forwardRef<RefSelectProps, CustomSelectProps>(
  (
    {
      allowClear = false,
      mode,
      width,
      height,
      border,
      bgColor,
      textColor,
      borderRadius,
      placeholder,
      placeholderColor,
      lineHeight,
      value,
      marginTop,
      options,
      onPopupScroll,
      boxShadow,
      disabled,
      disableOption = false,
      optionName,
      optionClassName = '',
      ...props
    },
    ref
  ) => {
    const { status, style: propsStyle, ...selectProps } = props;
    const resolvedBorder =
      status === 'error'
        ? '1px solid var(--danger)'
        : status === 'warning'
          ? '1px solid var(--warning)'
          : border || 'none';

    const shellStyle: React.CSSProperties = {
      display: 'inline-block',
      verticalAlign: 'middle',
      width: width || '100%',
      marginTop: marginTop,
      height: height || '36px',
      border: resolvedBorder,
      borderRadius: borderRadius || '8px',
      backgroundColor: legacyFillColor(bgColor) || 'var(--light-gray)',
      boxShadow: boxShadow || 'none',
      boxSizing: 'border-box',
      lineHeight: lineHeight || 'normal',
      // Real CSS (see .zl-custom-select in global.scss) reads this for placeholders.
      ['--zl-input-placeholder' as string]:
        placeholderColor || 'var(--text-secondary)',
    };

    const selectStyle: React.CSSProperties = {
      height: '100%',
      width: '100%',
      margin: 0,
      border: 'none',
      boxShadow: 'none',
      backgroundColor: 'transparent',
      color: legacyTextColor(textColor) || 'var(--text-primary)',
      ...(propsStyle && typeof propsStyle === 'object' ? propsStyle : {}),
    };

    return (
      <div className='zl-custom-select' style={shellStyle}>
        <Select
          ref={ref}
          allowClear={allowClear}
          mode={mode}
          value={value}
          placeholder={placeholder}
          disabled={disabled}
          onPopupScroll={onPopupScroll}
          status={status}
          {...selectProps}
          variant='borderless'
          style={selectStyle}
        >
          {options &&
            options.map((option, index) => {
              return (
                <Option
                  className={index === 0 ? optionClassName : ''}
                  key={index}
                  value={option.value}
                  label={option.label ?? String(option.value ?? '')}
                  disabled={
                    (disableOption && option?.value === optionName) ||
                    option?.disabled
                  }
                >
                  {option?.tooltipReason ? (
                    <Tooltip title={option.tooltipReason}>
                      <span>{option.label}</span>
                    </Tooltip>
                  ) : (
                    <span>{option.label}</span>
                  )}
                </Option>
              );
            })}
        </Select>
      </div>
    );
  }
);

CustomSelect.displayName = 'CustomSelect';

export default CustomSelect;
